// #region Imports
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import InboxIcon from "@mui/icons-material/Inbox";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import type { MRT_RowData, MRT_TableInstance } from "material-react-table";
import { useEffect, useState, type ReactNode, type RefObject } from "react";
import {
  describeColumnFilters,
  type DescribeColumnFilterValue,
} from "_shared/_views/tables/column-filter-description";
// #endregion

/**
 * The area a table shows where its rows would be, when it has none.
 *
 * It lives in the one cell MRT draws in place of the rows, so everything here has to be true of a
 * `<td>` that spans the table: it may not widen the table, and it may not leave the pane looking
 * abandoned below it. Only half the centring is here — the table is what gives this element the
 * height to be centred in, in `emptyTableFillsThePane`.
 *
 * `position: sticky` is the rule worth explaining. An empty table is still as wide as its header
 * asks for, so on a table with more columns than the pane can show, a message centred in the
 * table's own width is not on screen at all: measured 642px past the right edge of a 1200px pane,
 * with nothing in view to suggest where it went. Sticking to the leading edge of the scrollport and
 * taking the pane's width instead puts it in the middle of what the reader can actually see, at
 * every scroll offset.
 */
const EmptyStateContainer = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  justifyContent: "center",
  left: 0,
  // The table is never narrower than its container, so the measured width is the table's own width
  // in every case but the one this is here for. The clamp covers a measurement a resize behind.
  maxWidth: "100%",
  minHeight: theme.spacing(15),
  padding: theme.spacing(4, 2),
  position: "sticky",
  textAlign: "center",
}));

/**
 * The glyph over the message, and the one coloured thing in here.
 *
 * A failure is the only one of the three states the reader may have to do something about, and so
 * the only one wearing the palette that says so — the same argument colour gets on the row action
 * that destroys something. See [table row actions](../../../../../docs/software-design/table-row-actions.md).
 *
 * The colour is set on this wrapper rather than on the icon because a page can pass its own — an
 * illustration, not a `SvgIcon` — and because a rule on the container reaching in for the glyph
 * would be the more specific of the two and would quietly repaint the failed one grey.
 */
const StateIcon = styled("div", { shouldForwardProp: (prop) => prop !== "failed" })<{ failed?: boolean }>(
  ({ failed, theme }) => ({
    color: failed ? theme.palette.error.main : theme.palette.text.disabled,
    display: "flex",
    "& > .MuiSvgIcon-root": { fontSize: "2.5rem" },
  }),
);

/**
 * How wide the pane is, tracked rather than read once.
 *
 * The message takes this width to centre itself in the visible part of a table wider than its pane.
 * Reading `clientWidth` during render — which is what MRT's own fallback does — gets the width as it
 * was when the table last rendered, and a table with nothing in it has no reason to render again
 * when the window changes shape, so the message would stay where the stale width put it. The ref
 * itself is what is passed rather than its element: this component is a descendant of the container
 * and renders before the container's ref is attached, so during render there is nothing to read.
 */
function usePaneWidth(containerRef: RefObject<HTMLDivElement | null>): number | undefined {
  const [width, setWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    setWidth(container.clientWidth || undefined);
    // jsdom has no ResizeObserver, and a test rendering an empty table should not need one.
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setWidth(container.clientWidth || undefined));
    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef]);

  return width;
}

export type TableEmptyStateProps = {
  /**
   * What the table lists, plural and lower case — "contracts", "teams working for me". It is the
   * same phrase the search box is given, so the two read as one page: "Search contracts" above
   * "No contracts".
   */
  subject: string;
  /**
   * While the rows are on their way the area stays blank: MRT already draws a spinner over it.
   *
   * Pass the query's `isFetching` rather than its `isLoading`. `isLoading` is the first fetch only,
   * and every fetch after it — a keystroke in the search, a filter, a page — empties the table while
   * it is in flight, so a message drawn then says "no matching contracts" about rows that are on
   * their way, and flickers once per keystroke.
   */
  isLoading?: boolean;
  /** The fetch failed. Says so, and offers `onRetry` where the page has one. */
  isError?: boolean;
  /**
   * What went wrong, in the API's own words, replacing the generic second line of the failure
   * state.
   *
   * Read it off the rejected query with `getErrorMessageFromUnknown` rather than composing it
   * here. That returns the server's own prose where the response carried Problem Details, and the
   * app's standard "contact support and give them reference …" sentence where it did not — so a
   * real rejection almost always names itself, and `undefined` means the page had no rejection to
   * read at all. Never pass a raw status or an exception's `toString`: "Couldn't load team
   * templates" followed by `[object Object]` is worse than the sentence it replaced.
   */
  errorDescription?: string;
  /** Runs the failed fetch again. Without it the error state explains itself and offers nothing. */
  onRetry?: () => void;
  /**
   * Filters the table's own state does not carry — a page's chips, a toggle, a tab that narrows the
   * query. Without it a page whose chips have excluded everything says "No contracts", which reads
   * as an empty account rather than an over-narrow filter.
   */
  hasPageFilters?: boolean;
  /** Replaces the heading where "No contracts" is not what the page wants to say. */
  title?: string;
  /** A second line under the heading, for a page with something more useful to add. */
  description?: string;
  /** Replaces the icon over the nothing-here message — a page with an illustration passes it here. */
  icon?: ReactNode;
  /** Names a column filter's value where the page can, such as a person for a user id. */
  describeFilterValue?: DescribeColumnFilterValue;
};

/**
 * What a filtered table with no rows says under its heading: the column filters by name, so the
 * reader sees what to take off without opening the filter row (#6714), and then what to do.
 *
 * The search text is not repeated: the search box above already shows it, and a sentence that
 * quoted it would be a row of the table holding the very text the reader searched for.
 */
function filteredDescription<TData extends MRT_RowData>(
  table: MRT_TableInstance<TData>,
  describeFilterValue: DescribeColumnFilterValue | undefined,
): string {
  const filters = describeColumnFilters(table, describeFilterValue).map((filter) => filter.text);
  const advice = "Try a different search, or clear the filters.";
  return filters.length === 0 ? advice : `Filtered by ${filters.join("; ")}. ${advice}`;
}

/**
 * The three things a table with no rows can mean, told apart and said out loud.
 *
 * Every list page had its own answer to this and most had none at all: nineteen of the app's
 * twenty-nine tables fell through to MRT's own italic "No records to display", which is the same
 * sentence whether the account is new, the search matched nothing, or the request failed — and in
 * the last case the only other sign was a three-word banner above the toolbar with nothing to
 * press. The wording is derived from `subject` so a page cannot invent a fourth phrasing by
 * accident, and every part of it can still be overridden where a page genuinely has more to say.
 */
export function TableEmptyState<TData extends MRT_RowData>(
  props: Readonly<TableEmptyStateProps & { table: MRT_TableInstance<TData> }>,
) {
  // The table instance is one mutable object that MRT keeps and edits in place, so its identity is
  // the same on every render and everything this component reads off it — the search text, the
  // column filters — is invisible to the compiler. Memoized, it renders once and then says whatever
  // was true then: on contracts it went on saying "No contracts" through a search that matched
  // nothing, while the search box beside it, which is not memoized, had already grown its clear
  // button. The shared table wrapper carries the same directive for the same reason.
  "use no memo";

  const {
    description,
    describeFilterValue,
    errorDescription,
    hasPageFilters,
    icon,
    isError,
    isLoading,
    onRetry,
    subject,
    table,
    title,
  } = props;
  const paneWidth = usePaneWidth(table.refs.tableContainerRef);
  const { columnFilters, globalFilter } = table.getState();
  const isFiltered = Boolean(globalFilter) || columnFilters.length > 0 || Boolean(hasPageFilters);

  // Nothing at all while the rows are in flight. Returning undefined here would not do it: MRT
  // reads the fallback as `render?.({ table }) ?? <its own italic Typography>`, so a nullish answer
  // is the one way to get back the sentence this component exists to replace.
  //
  // A failure outranks it. Pages tend to derive "loading" from having no data yet — which is also
  // true, and stays true, after the request that would have brought it came back 500 — and reading
  // the flags in the other order left the failed table blank with nothing to press.
  if (isLoading && !isError) return <EmptyStateContainer aria-hidden style={{ width: paneWidth }} />;

  const state = isError
    ? {
        // The API's own words where it gave any, and the generic line where it did not. A failure
        // the reader can do something about — a permission they have to ask for, a filter the
        // server refused — is one they can only act on if it is named.
        description: errorDescription ?? "Something went wrong on the way. Try again in a moment.",
        icon: <ErrorOutlineIcon />,
        title: `Couldn't load ${subject}`,
      }
    : isFiltered
      ? {
          description: filteredDescription(table, describeFilterValue),
          icon: <SearchOffIcon />,
          title: `No matching ${subject}`,
        }
      : {
          description,
          icon: icon ?? <InboxIcon />,
          title: `No ${subject}`,
        };

  return (
    <EmptyStateContainer
      // Announced as it appears: a reader who cannot see the rows go away is otherwise told nothing
      // at all. `alert` for the failure, which is the one state they may have to act on.
      role={isError ? "alert" : "status"}
      style={{ width: paneWidth }}
    >
      <StateIcon failed={isError}>{state.icon}</StateIcon>
      <Typography component="h2" variant="h6">
        {title ?? state.title}
      </Typography>
      {state.description && (
        <Typography color="text.secondary" variant="body2">
          {state.description}
        </Typography>
      )}
      {isError && onRetry && (
        <Button onClick={onRetry} startIcon={<RefreshIcon />} sx={{ mt: 1 }} variant="outlined">
          Retry
        </Button>
      )}
    </EmptyStateContainer>
  );
}

/**
 * Builds a table's `renderEmptyRowsFallback` from what the page knows about its own query.
 *
 * ```tsx
 * renderEmptyRowsFallback: emptyRowsFallback({
 *   subject: "contracts",
 *   isLoading: documentsQuery.isFetching,
 *   isError: documentsQuery.isError,
 *   onRetry: () => void documentsQuery.refetch(),
 * }),
 * ```
 */
export function emptyRowsFallback<TData extends MRT_RowData>(props: Readonly<TableEmptyStateProps>) {
  return function EmptyRows({ table }: { table: MRT_TableInstance<TData> }) {
    return <TableEmptyState {...props} table={table} />;
  };
}
